<template>
  <a-modal
    title="对话管理"
    :visible="visible"
    :width="1500"
    :footer="null"
    @cancel="handleClose"
    class="dialogue-modal"
    :destroyOnClose="true"
  >
    <div class="dialogue-editor">
      <a-card class="left-panel">
        <div class="toolbar">
          <a-button type="primary" icon="plus" @click="handleAddNode">新建节点</a-button>
          <a-button icon="reload" @click="refreshDialogueTree">刷新</a-button>
        </div>

        <a-spin :spinning="loading">
          <a-tree
            v-if="treeData.length > 0"
            :tree-data="treeData"
            :replace-fields="{ children: 'children', title: 'title', key: 'key' }"
            :default-expand-all="true"
            :selectedKeys="selectedNode ? [selectedNode.id] : []"
            @select="onTreeSelect"
            class="tree-scroll"
          >
            <template slot="title" slot-scope="item">
              <div :class="{ 'selected-node-tree': selectedNode && selectedNode.id === item.id }" class="tree-node-title">
                <span class="ellipsis">{{ item.nodeName || ('节点 ' + item.id) }}</span>
                <a-tag v-if="item.isStart" color="green" class="mini-tag">起始</a-tag>
                <a-tag v-if="!item.nextNodeId && item.children && item.children.length > 0" color="blue" class="mini-tag">选择</a-tag>
                <a-tag v-if="!item.nextNodeId && (!item.children || item.children.length === 0)" color="red" class="mini-tag">结束</a-tag>
              </div>
            </template>
          </a-tree>

          <a-empty v-if="!loading && treeData.length === 0" description="暂无对话节点">
            <a-button type="primary" @click="handleAddNode">创建起始节点</a-button>
          </a-empty>
        </a-spin>
      </a-card>

      <a-card class="right-panel">
        <div v-if="!selectedNode" class="empty-state">
          <a-icon type="select" class="empty-icon" />
          <div>请选择左侧节点以查看/编辑</div>
        </div>

        <div v-else>
          <div class="header-row">
            <h3 class="title-row">
              <span>节点：{{ selectedNode.nodeName || ('节点 ' + selectedNode.id) }}</span>
              <a-tag :color="getNodeTypeColor(selectedNode.nodeType)" class="title-tag">{{ getNodeTypeText(selectedNode.nodeType) }}</a-tag>
              <a-tag v-if="selectedNode.isStart" color="green" class="title-tag">起始</a-tag>
            </h3>
            <div>
              <a-button type="link" icon="edit" @click="handleEditNode(selectedNode)">编辑</a-button>
              <a-button type="link" icon="delete" style="color:#f5222d;" @click="handleDeleteNode(selectedNode)">删除</a-button>
            </div>
          </div>

          <a-descriptions
            class="dialogue-node-descriptions"
            bordered
            size="small"
            :column="{ xxl: 2, xl: 2, lg: 2, md: 1, sm: 1, xs: 1 }"
            style="margin-bottom: 12px;"
          >
            <a-descriptions-item label="节点类型">
              <a-select
                :value="selectedNode.nodeType"
                style="width: 100%; max-width: 200px;"
                @change="(value) => updateSelectedNodeType(value)"
              >
                <a-select-option :value="1">文案</a-select-option>
                <a-select-option :value="2">语音</a-select-option>
                <a-select-option :value="3">视频</a-select-option>
                <a-select-option :value="4">电话</a-select-option>
              </a-select>
            </a-descriptions-item>
            <a-descriptions-item label="起始节点">
              <a-switch :checked="!!selectedNode.isStart" checked-children="是" un-checked-children="否" @change="updateSelectedIsStart" />
            </a-descriptions-item>
            <a-descriptions-item label="下一节点（线性）">
              <div style="display:flex;align-items:center;gap:8px;">
                <a-select
                  style="width: 100%; max-width: 260px;"
                  :value="selectedNode.nextNodeId"
                  allowClear
                  show-search
                  option-filter-prop="children"
                  placeholder="选择下一节点"
                  @change="(value) => updateNextNodeId(value)"
                >
                  <a-select-option
                    v-for="node in allNodes"
                    :key="node.id"
                    :value="node.id"
                    :disabled="isNextNodeDisabledByContext(selectedNode.id, selectedNode.nextNodeId, node.id)"
                  >
                    {{ node.nodeName || ('节点 ' + node.id) }}
                  </a-select-option>
                </a-select>
                <a-tag v-if="!selectedNode.nextNodeId" color="red">未设置</a-tag>
              </div>
            </a-descriptions-item>
            <a-descriptions-item label="节点动作">
              <a-select
                :value="selectedNode.actionType || 'NONE'"
                style="width: 100%; max-width: 220px;"
                @change="(value) => updateSelectedActionType(value)"
              >
                <a-select-option v-for="opt in actionTypeOptions" :key="opt.value" :value="opt.value">
                  {{ opt.label }}
                </a-select-option>
              </a-select>
            </a-descriptions-item>
            <a-descriptions-item label="价格">
              {{ selectedNode.price === undefined || selectedNode.price === null || selectedNode.price === '' ? 0 : selectedNode.price }}
            </a-descriptions-item>
          </a-descriptions>

          <a-alert
            v-if="isTextNode(selectedNode.nodeType)"
            message="文案分割符固定为 * ，例如：你好*我是校长"
            type="info"
            show-icon
            style="margin-bottom: 12px;"
          />

          <a-collapse :defaultActiveKey="['content']">
            <a-collapse-panel key="content" :header="contentPanelTitle(selectedNode.nodeType)">
              <div class="lang-grid lang-grid-single">
                <div v-for="lang in languageConfigs" :key="'content-' + lang.code" class="media-card">
                  <h4>{{ contentLabelPrefix(selectedNode.nodeType, lang.label) }}</h4>

                  <template v-if="isTextNode(selectedNode.nodeType)">
                    <div class="split-preview">
                      <template v-if="splitScriptByLang(selectedNode, lang.code).length > 0">
                        <div v-for="(part, idx) in splitScriptByLang(selectedNode, lang.code)" :key="idx" class="script-preview-item">
                          {{ idx + 1 }}. {{ part }}
                        </div>
                      </template>
                      <span v-else class="empty-tip">未配置文案</span>
                    </div>
                  </template>

                  <template v-else-if="isAudioNode(selectedNode.nodeType)">
                    <audio
                      v-if="selectedNode[lang.audioKey]"
                      :src="selectedNode[lang.audioKey]"
                      controls
                      class="audio-preview"
                    />
                    <a-empty v-else description="未上传语音" />
                  </template>

                  <template v-else>
                    <video
                      v-if="selectedNode[lang.videoKey]"
                      :src="selectedNode[lang.videoKey]"
                      controls
                      class="media-preview"
                    />
                    <a-empty v-else :description="isPhoneNode(selectedNode.nodeType) ? '未上传电话视频' : '未上传视频'" />
                  </template>
                </div>
              </div>
            </a-collapse-panel>
          </a-collapse>

          <a-divider style="margin: 16px 0 12px;">分支选项</a-divider>
          <a-list bordered :dataSource="selectedNode.options || []" style="max-height: 280px; overflow: auto;">
            <template slot="renderItem" slot-scope="opt, index">
              <a-list-item>
                <div style="display:flex;justify-content:space-between;align-items:center;width:100%;">
                  <div class="option-main">
                    <a-tag color="blue">{{ String.fromCharCode(65 + index) }}</a-tag>
                    <div class="option-content">
                      <div class="option-text" :title="getOptionDisplayName(opt) || '未命名选项'">
                        {{ getOptionDisplayName(opt) || '未命名选项' }}
                      </div>
                      <div class="option-target" :title="getNodeNameById(opt.nextNodeId)">
                        -> {{ getNodeNameById(opt.nextNodeId) }}
                      </div>
                    </div>
                  </div>
                  <div>
                    <a-button type="link" size="small" @click="openEditOptionModal(opt)">编辑</a-button>
                    <a-button type="link" size="small" style="color:#f5222d;" @click="removeOption(opt)">删除</a-button>
                  </div>
                </div>
              </a-list-item>
            </template>
          </a-list>
          <div style="margin-top:8px;">
            <a-button type="dashed" icon="plus" @click="openAddOptionModal">新增分支选项</a-button>
          </div>
        </div>
      </a-card>
    </div>

    <a-modal
      :title="nodeModal.title"
      :visible="nodeModal.visible"
      :width="1100"
      :confirm-loading="nodeModal.loading"
      @ok="submitNodeForm"
      @cancel="handleNodeModalCancel"
      :destroyOnClose="true"
    >
      <a-form-model ref="nodeFormRef" :model="nodeForm" :rules="nodeRules" layout="vertical">
        <a-row :gutter="16">
          <a-col :span="12">
            <a-form-model-item label="节点名称" prop="nodeName">
              <a-input v-model="nodeForm.nodeName" :maxLength="100" placeholder="仅用于后台识别" />
            </a-form-model-item>
          </a-col>
          <a-col :span="6">
            <a-form-model-item label="节点类型" prop="nodeType">
              <a-select v-model="nodeForm.nodeType">
                <a-select-option :value="1">文案</a-select-option>
                <a-select-option :value="2">语音</a-select-option>
                <a-select-option :value="3">视频</a-select-option>
                <a-select-option :value="4">电话</a-select-option>
              </a-select>
            </a-form-model-item>
          </a-col>
          <a-col :span="6">
            <a-form-model-item label="是否起始节点">
              <a-switch v-model="nodeForm.isStart" checked-children="是" un-checked-children="否" />
            </a-form-model-item>
          </a-col>
        </a-row>
        <a-row :gutter="16">
          <a-col :span="12">
            <a-form-model-item label="节点动作">
              <a-select v-model="nodeForm.actionType">
                <a-select-option v-for="opt in actionTypeOptions" :key="opt.value" :value="opt.value">
                  {{ opt.label }}
                </a-select-option>
              </a-select>
            </a-form-model-item>
          </a-col>
          <a-col :span="12">
            <a-form-model-item label="价格">
              <a-input-number
                v-model="nodeForm.price"
                :min="0"
                style="width: 100%;"
                placeholder="请输入价格，不填默认为0"
              />
            </a-form-model-item>
          </a-col>
        </a-row>

        <a-form-model-item label="下一节点（线性，不是分支）">
          <a-select
            v-model="nodeForm.nextNodeId"
            allowClear
            show-search
            option-filter-prop="children"
            placeholder="选择下一节点"
          >
            <a-select-option
              v-for="node in allNodes"
              :key="node.id"
              :value="node.id"
              :disabled="isNextNodeDisabledByContext(nodeForm.id, nodeForm.nextNodeId, node.id)"
            >
              {{ node.nodeName || ('节点 ' + node.id) }}
            </a-select-option>
          </a-select>
        </a-form-model-item>

        <a-divider>{{ contentPanelTitle(nodeForm.nodeType) }}</a-divider>
        <div class="lang-grid">
          <div v-for="lang in languageConfigs" :key="'edit-' + lang.code" class="media-card">
            <h4>{{ lang.label }}</h4>

            <a-form-model-item v-if="isTextNode(nodeForm.nodeType)" :label="lang.label + '文案'">
              <a-textarea
                v-model="nodeForm[lang.scriptKey]"
                :maxLength="2000"
                :rows="3"
                placeholder="使用 * 分割，例如：你好*我是校长"
              />
            </a-form-model-item>

            <a-form-model-item v-if="isVideoLikeNode(nodeForm.nodeType)" :label="lang.label + (isPhoneNode(nodeForm.nodeType) ? '电话视频' : '视频')">
              <a-upload
                :custom-request="(options) => handleMediaUpload(options, lang.videoKey, 'video')"
                :before-upload="(file) => beforeMediaUpload(file, 'video')"
                :show-upload-list="false"
                accept=".mp4,.mov,.avi,.mkv"
              >
                <a-button><a-icon type="upload" />上传{{ isPhoneNode(nodeForm.nodeType) ? '电话视频' : '视频' }}</a-button>
              </a-upload>
              <div class="upload-url">{{ nodeForm[lang.videoKey] || '未上传' }}</div>
            </a-form-model-item>

            <a-form-model-item v-if="isAudioNode(nodeForm.nodeType)" :label="lang.label + '语音'">
              <a-upload
                :custom-request="(options) => handleMediaUpload(options, lang.audioKey, 'audio')"
                :before-upload="(file) => beforeMediaUpload(file, 'audio')"
                :show-upload-list="false"
                accept=".mp3,.m4a,.wav,.ogg"
              >
                <a-button><a-icon type="upload" />上传语音</a-button>
              </a-upload>
              <div class="upload-url">{{ nodeForm[lang.audioKey] || '未上传' }}</div>
            </a-form-model-item>
          </div>
        </div>
      </a-form-model>
    </a-modal>

    <a-modal
      :title="optionModal.title"
      :visible="optionModal.visible"
      :confirm-loading="optionModal.loading"
      @ok="submitOptionForm"
      @cancel="handleOptionModalCancel"
      :destroyOnClose="true"
      width="520"
      class="option-modal"
      :bodyStyle="{ maxHeight: '60vh', overflowY: 'auto' }"
    >
      <div class="option-form-single">
      <a-form-model ref="optionFormRef" :model="optionForm" :rules="optionRules" layout="vertical">
        <a-form-model-item label="中文选项文案" prop="optionNameZh">
          <a-input v-model="optionForm.optionNameZh" :maxLength="200" placeholder="例如：同意生成合照" />
        </a-form-model-item>
        <a-form-model-item label="英文选项文案">
          <a-input v-model="optionForm.optionNameEn" :maxLength="200" />
        </a-form-model-item>
        <a-form-model-item label="日语选项文案">
          <a-input v-model="optionForm.optionNameJa" :maxLength="200" />
        </a-form-model-item>
        <a-form-model-item label="印尼语选项文案">
          <a-input v-model="optionForm.optionNameId" :maxLength="200" />
        </a-form-model-item>
        <a-form-model-item label="葡语选项文案">
          <a-input v-model="optionForm.optionNamePt" :maxLength="200" />
        </a-form-model-item>
        <a-form-model-item label="西语选项文案">
          <a-input v-model="optionForm.optionNameEs" :maxLength="200" />
        </a-form-model-item>
        <a-form-model-item label="跳转节点" prop="nextNodeId">
          <a-select
            v-model="optionForm.nextNodeId"
            allowClear
            show-search
            option-filter-prop="children"
            placeholder="选择选项指向的下一节点"
          >
            <a-select-option
              v-for="node in allNodes"
              :key="node.id"
              :value="node.id"
              :disabled="isNextNodeDisabledByContext(selectedNode && selectedNode.id, selectedNode && selectedNode.nextNodeId, node.id)"
            >
              {{ node.nodeName || ('节点 ' + node.id) }}
            </a-select-option>
          </a-select>
        </a-form-model-item>
      </a-form-model>
      </div>
    </a-modal>
  </a-modal>
</template>

<script>
import UploadFileToVOD from '@/api/vod.js'
import { addDialogueNode, delDialogueNode, getDialogueTree, updateDialogueNode } from '@/api/video/dialogueNode'

const NODE_TYPE_TEXT = 1
const NODE_TYPE_AUDIO = 2
const NODE_TYPE_VIDEO = 3
const NODE_TYPE_PHONE = 4

const LANG_CONFIGS = [
  { code: 'zh', label: '中文', scriptKey: 'scriptZh', videoKey: 'videoUrl', audioKey: 'audioUrl' },
  { code: 'en', label: '英文', scriptKey: 'scriptEn', videoKey: 'videoUrlEn', audioKey: 'audioUrlEn' },
  { code: 'ja', label: '日语', scriptKey: 'scriptJa', videoKey: 'videoUrlJa', audioKey: 'audioUrlJa' },
  { code: 'id', label: '印尼语', scriptKey: 'scriptId', videoKey: 'videoUrlId', audioKey: 'audioUrlId' },
  { code: 'pt', label: '葡语', scriptKey: 'scriptPt', videoKey: 'videoUrlPt', audioKey: 'audioUrlPt' },
  { code: 'es', label: '西语', scriptKey: 'scriptEs', videoKey: 'videoUrlEs', audioKey: 'audioUrlEs' }
]

const ACTION_TYPE_OPTIONS = [
  { value: 'NONE', label: '无动作' },
  { value: 'GENERATE_PHOTO', label: '生成合照' },
  { value: 'GENERATE_VIDEO', label: '生成视频' }
]

function normalizeNodeType(rawType) {
  if (rawType === undefined || rawType === null || rawType === '') return NODE_TYPE_TEXT
  if (typeof rawType === 'string') {
    const lower = rawType.toLowerCase()
    if (lower === 'text' || lower === 'script' || lower === '文案') return NODE_TYPE_TEXT
    if (lower === 'audio' || lower === '语音') return NODE_TYPE_AUDIO
    if (lower === 'video' || lower === '视频') return NODE_TYPE_VIDEO
    if (lower === 'phone' || lower === '电话') return NODE_TYPE_PHONE
  }
  const num = Number(rawType)
  if ([NODE_TYPE_TEXT, NODE_TYPE_AUDIO, NODE_TYPE_VIDEO, NODE_TYPE_PHONE].includes(num)) {
    return num
  }
  return NODE_TYPE_TEXT
}

function createEmptyNodeForm() {
  return {
    id: null,
    characterId: null,
    nodeName: '',
    isStart: false,
    nodeType: NODE_TYPE_TEXT,
    actionType: 'NONE',
    price: 0,
    nextNodeId: null,
    scriptZh: '',
    scriptEn: '',
    scriptJa: '',
    scriptId: '',
    scriptPt: '',
    scriptEs: '',
    videoUrl: '',
    videoUrlEn: '',
    videoUrlJa: '',
    videoUrlId: '',
    videoUrlPt: '',
    videoUrlEs: '',
    audioUrl: '',
    audioUrlEn: '',
    audioUrlJa: '',
    audioUrlId: '',
    audioUrlPt: '',
    audioUrlEs: ''
  }
}

function createEmptyOptionForm() {
  return {
    id: null,
    optionNameZh: '',
    optionNameEn: '',
    optionNameJa: '',
    optionNameId: '',
    optionNamePt: '',
    optionNameEs: '',
    nextNodeId: null
  }
}

export default {
  name: 'DialogueManagement',
  props: {
    visible: { type: Boolean, default: false },
    characterId: { type: Number, required: true },
    characterName: { type: String, required: true }
  },
  data() {
    return {
      loading: false,
      allNodes: [],
      treeData: [],
      selectedNode: null,
      languageConfigs: LANG_CONFIGS,
      actionTypeOptions: ACTION_TYPE_OPTIONS,
      nodeModal: {
        visible: false,
        title: '添加节点',
        loading: false,
        mode: 'add'
      },
      optionModal: {
        visible: false,
        loading: false,
        mode: 'add',
        title: '新增选项'
      },
      nodeForm: createEmptyNodeForm(),
      optionForm: createEmptyOptionForm(),
      nodeRules: {
        nodeName: [{ required: true, message: '请输入节点名称', trigger: 'blur' }],
        nodeType: [{ required: true, message: '请选择节点类型', trigger: 'change' }]
      },
      optionRules: {
        optionNameZh: [{ required: true, message: '请输入中文选项文案', trigger: 'blur' }],
        nextNodeId: [{ required: true, message: '请选择下一节点', trigger: 'change' }]
      }
    }
  },
  watch: {
    visible(val) {
      if (val && this.characterId) {
        this.init()
      }
    }
  },
  methods: {
    init() {
      this.fetchDialogueTree()
    },
    normalizeNode(rawNode) {
      const node = { ...(rawNode || {}) }
      const normalized = {
        ...node,
        isStart: !!(node.isStart ?? node.startNode),
        nextNodeId: node.nextNodeId ?? node.jumpNodeId ?? null,
        nodeType: normalizeNodeType(node.nodeType ?? node.type),
        actionType: node.actionType || 'NONE',
        price: node.price === undefined || node.price === null || node.price === '' ? 0 : Number(node.price) || 0,
        nodeName: node.nodeName || `节点 ${node.id}`,
        options: Array.isArray(node.options)
          ? node.options.map((opt, index) => ({
            id: opt.id || `${node.id || 'node'}-opt-${index + 1}`,
            optionNameZh: opt.optionNameZh || opt.optionName || opt.text || '',
            optionNameEn: opt.optionNameEn || '',
            optionNameJa: opt.optionNameJa || '',
            optionNameId: opt.optionNameId || '',
            optionNamePt: opt.optionNamePt || '',
            optionNameEs: opt.optionNameEs || '',
            nextNodeId: opt.nextNodeId ?? null
          }))
          : []
      }

      LANG_CONFIGS.forEach((lang) => {
        if (normalized[lang.scriptKey] === undefined || normalized[lang.scriptKey] === null) {
          normalized[lang.scriptKey] = lang.code === 'zh' ? (node.content || '') : ''
        }
        if (normalized[lang.videoKey] === undefined || normalized[lang.videoKey] === null) {
          normalized[lang.videoKey] = ''
        }
        if (normalized[lang.audioKey] === undefined || normalized[lang.audioKey] === null) {
          normalized[lang.audioKey] = ''
        }
      })

      return normalized
    },
    async fetchDialogueTree() {
      this.loading = true
      try {
        const { data } = await getDialogueTree(this.characterId)
        this.allNodes = (data || []).map(this.normalizeNode)
        this.buildTreeData()

        const oldSelectedId = this.selectedNode && this.selectedNode.id
        if (oldSelectedId) {
          const sameNode = this.allNodes.find((n) => n.id === oldSelectedId)
          this.selectedNode = sameNode || null
        }
        if (!this.selectedNode) {
          this.selectedNode = this.allNodes.find((n) => n.isStart) || this.allNodes[0] || null
        }
      } catch (error) {
        console.error('获取对话树失败:', error)
        this.$message.error('获取对话树失败')
      } finally {
        this.loading = false
      }
    },
    buildTreeData() {
      if (!this.allNodes.length) {
        this.treeData = []
        return
      }
      const nodeMap = this.allNodes.reduce((acc, n) => {
        acc[n.id] = n
        return acc
      }, {})
      const rootNode = this.allNodes.find((n) => n.isStart)
      const resultTree = []

      if (rootNode) {
        const builtRoot = this.buildLinearTree(rootNode.id, nodeMap)
        if (builtRoot) {
          resultTree.push(builtRoot)
        }
      }

      const referencedIds = new Set()
      this.allNodes.forEach((node) => {
        if (node.nextNodeId !== null && node.nextNodeId !== undefined && node.nextNodeId !== '') {
          referencedIds.add(String(node.nextNodeId))
        }
        if (Array.isArray(node.options)) {
          node.options.forEach((opt) => {
            if (opt.nextNodeId !== null && opt.nextNodeId !== undefined && opt.nextNodeId !== '') {
              referencedIds.add(String(opt.nextNodeId))
            }
          })
        }
      })

      this.allNodes.forEach((node) => {
        const isRoot = rootNode && rootNode.id === node.id
        const isReferenced = referencedIds.has(String(node.id))
        const existsInTree = resultTree.some((root) => root.key === node.id)
        if (!isRoot && !isReferenced && !existsInTree) {
          resultTree.push({
            ...node,
            key: node.id,
            title: this.getNodeLabel(node),
            children: []
          })
        }
      })

      this.treeData = resultTree
    },
    buildLinearTree(nodeId, nodeMap, visited = new Set()) {
      const currentNode = nodeMap[nodeId]
      if (!currentNode || visited.has(String(nodeId))) {
        return null
      }
      visited.add(String(nodeId))
      const treeNode = {
        ...currentNode,
        key: currentNode.id,
        title: this.getNodeLabel(currentNode),
        children: []
      }
      if (currentNode.nextNodeId && nodeMap[currentNode.nextNodeId]) {
        const childTree = this.buildLinearTree(currentNode.nextNodeId, nodeMap, new Set(visited))
        if (childTree) {
          treeNode.children.push(childTree)
        }
      }
      if (Array.isArray(currentNode.options)) {
        currentNode.options.forEach((opt) => {
          if (!opt.nextNodeId || !nodeMap[opt.nextNodeId]) return
          const optChildTree = this.buildLinearTree(opt.nextNodeId, nodeMap, new Set(visited))
          if (optChildTree) {
            treeNode.children.push({
              ...optChildTree,
              title: `[选项] ${this.getOptionDisplayName(opt) || '未命名选项'} -> ${optChildTree.nodeName || ('节点 ' + optChildTree.id)}`
            })
          }
        })
      }
      return treeNode
    },
    getNodeTypeText(type) {
      const typeMap = {
        [NODE_TYPE_TEXT]: '文案',
        [NODE_TYPE_AUDIO]: '语音',
        [NODE_TYPE_VIDEO]: '视频',
        [NODE_TYPE_PHONE]: '电话'
      }
      return typeMap[normalizeNodeType(type)] || '未知'
    },
    getNodeTypeColor(type) {
      const colorMap = {
        [NODE_TYPE_TEXT]: 'blue',
        [NODE_TYPE_AUDIO]: 'cyan',
        [NODE_TYPE_VIDEO]: 'purple',
        [NODE_TYPE_PHONE]: 'red'
      }
      return colorMap[normalizeNodeType(type)] || 'default'
    },
    isTextNode(type) {
      return normalizeNodeType(type) === NODE_TYPE_TEXT
    },
    isAudioNode(type) {
      return normalizeNodeType(type) === NODE_TYPE_AUDIO
    },
    isVideoNode(type) {
      return normalizeNodeType(type) === NODE_TYPE_VIDEO
    },
    isPhoneNode(type) {
      return normalizeNodeType(type) === NODE_TYPE_PHONE
    },
    isVideoLikeNode(type) {
      return this.isVideoNode(type) || this.isPhoneNode(type)
    },
    contentPanelTitle(type) {
      if (this.isTextNode(type)) return '多语言文案（本地化）'
      if (this.isAudioNode(type)) return '多语言语音（本地化）'
      if (this.isPhoneNode(type)) return '多语言电话视频（本地化）'
      return '多语言视频（本地化）'
    },
    contentLabelPrefix(type, langLabel) {
      if (this.isTextNode(type)) return `${langLabel}文案`
      if (this.isAudioNode(type)) return `${langLabel}语音`
      if (this.isPhoneNode(type)) return `${langLabel}电话视频`
      return `${langLabel}视频`
    },
    getNodeLabel(node) {
      return `[${this.getNodeTypeText(node.nodeType)}] ${node.nodeName || ('节点 ' + node.id)}`
    },
    getNodeNameById(nodeId) {
      const node = this.allNodes.find((item) => String(item.id) === String(nodeId))
      return node ? (node.nodeName || `节点 ${node.id}`) : `未知节点(${nodeId || '-'})`
    },
    getOptionDisplayName(option) {
      if (!option) return ''
      return option.optionNameZh || option.optionName || option.text || ''
    },
    splitScriptByLang(node, langCode) {
      const lang = LANG_CONFIGS.find((item) => item.code === langCode)
      if (!lang) return []
      const raw = node && node[lang.scriptKey]
      if (!raw) return []
      return String(raw)
        .split('*')
        .map((item) => item.trim())
        .filter(Boolean)
    },
    onTreeSelect(selectedKeys, info) {
      if (!selectedKeys.length || !info.node || !info.node.dataRef) {
        this.selectedNode = null
        return
      }
      this.selectedNode = info.node.dataRef
    },
    refreshDialogueTree() {
      this.fetchDialogueTree()
    },
    handleClose() {
      this.$emit('close')
    },
    handleAddNode() {
      this.nodeModal.mode = 'add'
      this.nodeModal.title = '添加节点'
      this.nodeForm = createEmptyNodeForm()
      this.nodeForm.characterId = this.characterId
      this.nodeModal.visible = true
      this.$nextTick(() => {
        if (this.$refs.nodeFormRef) {
          this.$refs.nodeFormRef.clearValidate()
        }
      })
    },
    handleEditNode(node) {
      this.nodeModal.mode = 'edit'
      this.nodeModal.title = '编辑节点'
      this.nodeForm = this.normalizeNode({ ...node, characterId: node.characterId || this.characterId })
      this.nodeModal.visible = true
      this.$nextTick(() => {
        if (this.$refs.nodeFormRef) {
          this.$refs.nodeFormRef.clearValidate()
        }
      })
    },
    async handleDeleteNode(node) {
      this.$confirm({
        title: '警告',
        content: `确定删除节点 "${this.getNodeLabel(node)}" 吗？`,
        okText: '确定',
        okType: 'danger',
        cancelText: '取消',
        onOk: async () => {
          try {
            await delDialogueNode(node.id)
            this.$message.success('删除成功')
            this.selectedNode = null
            this.fetchDialogueTree()
          } catch (error) {
            console.error('删除节点失败:', error)
            this.$message.error('删除失败')
          }
        }
      })
    },
    isNextNodeDisabledByContext(currentId, currentNextNodeId, candidateId) {
      if (candidateId === null || candidateId === undefined || candidateId === '') return false
      const currentKey = currentId ? String(currentId) : null
      const candidateKey = String(candidateId)
      if (currentKey && currentKey === candidateKey) return true
      if (currentKey && this.wouldCreateCycle(currentId, candidateId)) return true
      return false
    },
    wouldCreateCycle(fromId, toId) {
      if (!fromId || !toId) return false
      const target = String(fromId)
      const start = String(toId)
      const adj = {}
      const addEdge = (from, to) => {
        if (to === null || to === undefined || to === '') return
        const key = String(from)
        if (!adj[key]) adj[key] = []
        adj[key].push(String(to))
      }

      this.allNodes.forEach((node) => {
        const edgeTo = node.id === fromId ? toId : node.nextNodeId
        addEdge(node.id, edgeTo)
        if (Array.isArray(node.options)) {
          node.options.forEach((opt) => addEdge(node.id, opt.nextNodeId))
        }
      })

      const queue = [start]
      const visited = new Set()
      while (queue.length) {
        const current = queue.shift()
        if (current === target) return true
        if (visited.has(current)) continue
        visited.add(current)
        const nexts = adj[current] || []
        nexts.forEach((next) => {
          if (!visited.has(next)) queue.push(next)
        })
      }
      return false
    },
    hasAnotherStartNode(currentId) {
      return this.allNodes.some((node) => node.isStart && node.id !== currentId)
    },
    async updateSelectedIsStart(checked) {
      if (!this.selectedNode) return
      if (checked && this.hasAnotherStartNode(this.selectedNode.id)) {
        this.$message.warning('只能有一个起始节点，请先取消其他起始节点')
        return
      }
      const oldValue = this.selectedNode.isStart
      this.$set(this.selectedNode, 'isStart', checked)
      try {
        await updateDialogueNode(this.buildPayload({ ...this.selectedNode, isStart: checked }, true))
        this.$message.success('起始节点更新成功')
        this.fetchDialogueTree()
      } catch (error) {
        this.$set(this.selectedNode, 'isStart', oldValue)
        this.$message.error('更新失败')
      }
    },
    async updateSelectedNodeType(newType) {
      if (!this.selectedNode) return
      const normalizedType = normalizeNodeType(newType)
      const oldType = this.selectedNode.nodeType
      this.$set(this.selectedNode, 'nodeType', normalizedType)
      try {
        await updateDialogueNode(this.buildPayload({ ...this.selectedNode, nodeType: normalizedType }, true))
        this.$message.success('节点类型更新成功')
        this.fetchDialogueTree()
      } catch (error) {
        this.$set(this.selectedNode, 'nodeType', oldType)
        this.$message.error('更新失败')
      }
    },
    async updateSelectedActionType(newActionType) {
      if (!this.selectedNode) return
      const oldActionType = this.selectedNode.actionType || 'NONE'
      this.$set(this.selectedNode, 'actionType', newActionType || 'NONE')
      try {
        await updateDialogueNode(this.buildPayload({ ...this.selectedNode, actionType: this.selectedNode.actionType }, true))
        this.$message.success('节点动作更新成功')
        this.fetchDialogueTree()
      } catch (error) {
        this.$set(this.selectedNode, 'actionType', oldActionType)
        this.$message.error('更新失败')
      }
    },
    openAddOptionModal() {
      if (!this.selectedNode) {
        this.$message.warning('请先选择节点')
        return
      }
      this.optionModal.mode = 'add'
      this.optionModal.title = '新增分支选项'
      this.optionForm = createEmptyOptionForm()
      this.optionModal.visible = true
      this.$nextTick(() => {
        if (this.$refs.optionFormRef) {
          this.$refs.optionFormRef.clearValidate()
        }
      })
    },
    openEditOptionModal(option) {
      this.optionModal.mode = 'edit'
      this.optionModal.title = '编辑分支选项'
      this.optionForm = {
        id: option.id,
        optionNameZh: option.optionNameZh || option.optionName || '',
        optionNameEn: option.optionNameEn || '',
        optionNameJa: option.optionNameJa || '',
        optionNameId: option.optionNameId || '',
        optionNamePt: option.optionNamePt || '',
        optionNameEs: option.optionNameEs || '',
        nextNodeId: option.nextNodeId || null
      }
      this.optionModal.visible = true
      this.$nextTick(() => {
        if (this.$refs.optionFormRef) {
          this.$refs.optionFormRef.clearValidate()
        }
      })
    },
    async submitOptionForm() {
      if (!this.selectedNode) return
      this.$refs.optionFormRef.validate(async (valid) => {
        if (!valid) return
        const nextNodeId = this.optionForm.nextNodeId
        if (!nextNodeId) {
          this.$message.error('请选择选项跳转节点')
          return
        }
        if (this.isNextNodeDisabledByContext(this.selectedNode.id, this.selectedNode.nextNodeId, nextNodeId)) {
          this.$message.error('该选项指向会造成循环或自引用')
          return
        }

        this.optionModal.loading = true
        try {
          const options = Array.isArray(this.selectedNode.options) ? [...this.selectedNode.options] : []
          if (this.optionModal.mode === 'add') {
            options.push({
              optionNameZh: this.optionForm.optionNameZh.trim(),
              optionNameEn: (this.optionForm.optionNameEn || '').trim(),
              optionNameJa: (this.optionForm.optionNameJa || '').trim(),
              optionNameId: (this.optionForm.optionNameId || '').trim(),
              optionNamePt: (this.optionForm.optionNamePt || '').trim(),
              optionNameEs: (this.optionForm.optionNameEs || '').trim(),
              nextNodeId
            })
          } else {
            const idx = options.findIndex((opt) => String(opt.id) === String(this.optionForm.id))
            if (idx >= 0) {
              options[idx] = {
                ...options[idx],
                optionNameZh: this.optionForm.optionNameZh.trim(),
                optionNameEn: (this.optionForm.optionNameEn || '').trim(),
                optionNameJa: (this.optionForm.optionNameJa || '').trim(),
                optionNameId: (this.optionForm.optionNameId || '').trim(),
                optionNamePt: (this.optionForm.optionNamePt || '').trim(),
                optionNameEs: (this.optionForm.optionNameEs || '').trim(),
                nextNodeId
              }
            }
          }

          await this.persistNodeOptions(options)
          this.optionModal.visible = false
          this.optionForm = createEmptyOptionForm()
        } catch (error) {
          console.error('保存分支选项失败:', error)
          this.$message.error(error.message || '保存分支选项失败')
        } finally {
          this.optionModal.loading = false
        }
      })
    },
    async removeOption(option) {
      if (!this.selectedNode) return
      this.$confirm({
        title: '警告',
        content: `确定删除选项 "${this.getOptionDisplayName(option) || '未命名选项'}" 吗？`,
        okText: '确定',
        okType: 'danger',
        cancelText: '取消',
        onOk: async () => {
          const options = (this.selectedNode.options || []).filter((item) => String(item.id) !== String(option.id))
          await this.persistNodeOptions(options)
        }
      })
    },
    async persistNodeOptions(options) {
      if (!this.selectedNode) return
      const normalizedOptions = options.map((opt) => {
        const item = {
          optionNameZh: (opt.optionNameZh || opt.optionName || '').trim(),
          optionNameEn: (opt.optionNameEn || '').trim(),
          optionNameJa: (opt.optionNameJa || '').trim(),
          optionNameId: (opt.optionNameId || '').trim(),
          optionNamePt: (opt.optionNamePt || '').trim(),
          optionNameEs: (opt.optionNameEs || '').trim(),
          nextNodeId: opt.nextNodeId || null
        }
        if (opt.id) {
          item.id = opt.id
        }
        return item
      }).filter((opt) => opt.optionNameZh && opt.nextNodeId)

      const updatedNode = {
        ...this.selectedNode,
        options: normalizedOptions
      }
      await updateDialogueNode(this.buildPayload(updatedNode, true))
      this.$message.success('分支选项保存成功')
      this.fetchDialogueTree()
    },
    handleOptionModalCancel() {
      this.optionModal.visible = false
      this.optionForm = createEmptyOptionForm()
    },
    async updateNextNodeId(newNextNodeId) {
      if (!this.selectedNode) return
      if (newNextNodeId && this.isNextNodeDisabledByContext(this.selectedNode.id, this.selectedNode.nextNodeId, newNextNodeId)) {
        this.$message.error('该节点不能指向自己或形成循环引用')
        return
      }
      const oldNext = this.selectedNode.nextNodeId
      this.$set(this.selectedNode, 'nextNodeId', newNextNodeId)
      try {
        await updateDialogueNode(this.buildPayload({ ...this.selectedNode, nextNodeId: newNextNodeId }, true))
        this.$message.success('下一节点更新成功')
        this.fetchDialogueTree()
      } catch (error) {
        this.$set(this.selectedNode, 'nextNodeId', oldNext)
        this.$message.error('更新失败')
      }
    },
    beforeMediaUpload(file, mediaType) {
      const isVideo = /\.(mp4|mov|avi|mkv)$/i.test(file.name)
      const isAudio = /\.(mp3|m4a|wav|ogg)$/i.test(file.name)
      const fileSize = file.size / 1024 / 1024
      if (mediaType === 'video') {
        if (!isVideo) {
          this.$message.error('只能上传视频文件')
          return false
        }
        if (fileSize > 100) {
          this.$message.error('视频大小不能超过 100MB')
          return false
        }
      }
      if (mediaType === 'audio') {
        if (!isAudio) {
          this.$message.error('只能上传音频文件')
          return false
        }
        if (fileSize > 20) {
          this.$message.error('语音大小不能超过 20MB')
          return false
        }
      }
      return true
    },
    handleMediaUpload({ file, onSuccess, onError }, fieldKey, mediaType) {
      new UploadFileToVOD([file], {
        success: (urlList) => {
          const uploadedUrl = urlList && urlList[0] ? urlList[0] : ''
          this.$set(this.nodeForm, fieldKey, uploadedUrl)
          onSuccess({}, file)
          this.$message.success(mediaType === 'video' ? '视频上传成功' : '语音上传成功')
        },
        error: () => {
          onError(new Error('上传失败'))
          this.$message.error('上传失败')
        }
      })
    },
    buildPayload(source, partial = false) {
      const nodeType = normalizeNodeType(source.nodeType)
      const payload = {
        id: source.id,
        characterId: source.characterId || this.characterId,
        nodeName: source.nodeName,
        isStart: !!source.isStart,
        nodeType,
        nextNodeId: source.nextNodeId || null,
        actionType: source.actionType || 'NONE',
        price: source.price === undefined || source.price === null || source.price === '' ? 0 : Number(source.price) || 0,
        options: Array.isArray(source.options)
          ? source.options.map((opt) => ({
            id: opt.id,
            optionNameZh: opt.optionNameZh || opt.optionName || '',
            optionNameEn: opt.optionNameEn || '',
            optionNameJa: opt.optionNameJa || '',
            optionNameId: opt.optionNameId || '',
            optionNamePt: opt.optionNamePt || '',
            optionNameEs: opt.optionNameEs || '',
            optionName: opt.optionNameZh || opt.optionName || '',
            nextNodeId: opt.nextNodeId || null
          }))
          : []
      }

      LANG_CONFIGS.forEach((lang) => {
        payload[lang.scriptKey] = source[lang.scriptKey] || ''
        payload[lang.videoKey] = source[lang.videoKey] || ''
        payload[lang.audioKey] = source[lang.audioKey] || ''
      })

      // 兼容旧后端字段，便于平滑迁移
      payload.startNode = payload.isStart
      payload.jumpNodeId = payload.nextNodeId
      if (nodeType === NODE_TYPE_TEXT) {
        payload.content = payload.scriptZh || ''
      } else if (nodeType === NODE_TYPE_AUDIO) {
        payload.content = payload.audioUrl || ''
      } else {
        payload.content = payload.videoUrl || ''
      }

      if (partial) {
        return payload
      }

      return payload
    },
    submitNodeForm() {
      this.$refs.nodeFormRef.validate(async (valid) => {
        if (!valid) return

        if (this.nodeForm.isStart && this.hasAnotherStartNode(this.nodeForm.id)) {
          this.$message.error('只能有一个起始节点，请先取消其他起始节点')
          return
        }

        if (this.nodeForm.nextNodeId && this.isNextNodeDisabledByContext(this.nodeForm.id, null, this.nodeForm.nextNodeId)) {
          this.$message.error('下一节点配置无效')
          return
        }

        const payload = this.buildPayload(this.nodeForm)
        this.nodeModal.loading = true
        try {
          if (this.nodeModal.mode === 'add') {
            await addDialogueNode(payload)
            this.$message.success('添加成功')
          } else {
            await updateDialogueNode(payload)
            this.$message.success('更新成功')
          }
          this.nodeModal.visible = false
          this.fetchDialogueTree()
        } catch (error) {
          console.error('保存节点失败:', error)
          this.$message.error(error.message || '保存失败')
        } finally {
          this.nodeModal.loading = false
        }
      })
    },
    handleNodeModalCancel() {
      this.nodeModal.visible = false
      this.nodeForm = createEmptyNodeForm()
    }
  }
}
</script>

<style scoped>
.dialogue-modal>>>.ant-modal-body {
  height: 82vh;
  overflow: hidden;
}

.dialogue-editor {
  display: flex;
  height: 100%;
}

.left-panel {
  width: 450px;
  margin-right: 16px;
  overflow: auto;
  height: 100%;
}

.right-panel {
  flex: 1;
  overflow: auto;
  height: 100%;
}

.toolbar {
  display: flex;
  gap: 8px;
  margin-bottom: 16px;
}

.tree-scroll {
  max-height: 650px;
  overflow: auto;
}

.tree-node-title {
  display: flex;
  align-items: center;
  width: 100%;
}

.ellipsis {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.mini-tag {
  margin-left: 6px;
  font-size: 10px;
}

.selected-node-tree {
  background-color: #e6f7ff;
  border-left: 3px solid #1890ff;
  padding: 4px 8px;
  margin: -4px -8px;
  display: block;
}

:deep(.ant-tree-node-content-wrapper.ant-tree-node-selected) {
  background-color: transparent !important;
}

.empty-state {
  padding: 24px;
  color: #666;
  text-align: center;
}

.empty-icon {
  font-size: 48px;
  color: #d9d9d9;
  margin-bottom: 12px;
}

.header-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.title-row {
  margin: 0;
  display: flex;
  align-items: center;
}

.title-tag {
  margin-left: 8px;
}

.dialogue-node-descriptions :deep(.ant-descriptions-item-label) {
  min-width: 88px;
  white-space: nowrap;
  word-break: keep-all;
}

.dialogue-node-descriptions :deep(.ant-descriptions-item-content) {
  word-break: normal;
}

.lang-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.lang-grid-single {
  display: block;
}

.lang-grid-single .media-card {
  min-width: 0;
  width: 100%;
  margin-bottom: 10px;
}

.media-card {
  flex: 1 1 calc(33.33% - 12px);
  min-width: 290px;
  border: 1px solid #f0f0f0;
  border-radius: 4px;
  padding: 10px;
}

.media-preview {
  width: 100%;
  max-height: 180px;
  background: #000;
  border-radius: 4px;
}

.audio-preview {
  width: 100%;
}

.split-preview {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.script-preview-item {
  width: 100%;
  padding: 4px 8px;
  background: #e6f7ff;
  border: 1px solid #91d5ff;
  border-radius: 4px;
  color: #0050b3;
  line-height: 20px;
  word-break: break-word;
  overflow-wrap: anywhere;
}

.empty-tip {
  color: #999;
}

.upload-url {
  margin-top: 8px;
  font-size: 12px;
  color: #666;
  word-break: break-all;
}

.option-main {
  display: flex;
  align-items: flex-start;
  min-width: 0;
  flex: 1;
  margin-right: 8px;
  overflow: hidden;
  gap: 6px;
}

.option-content {
  min-width: 0;
  flex: 1;
  overflow: hidden;
}

.option-text {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  line-height: 20px;
  max-height: 40px;
  word-break: break-word;
  overflow-wrap: anywhere;
}

.option-target {
  color: #999;
  margin-top: 4px;
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.option-modal :deep(.ant-modal) {
  max-width: 520px;
}

.option-form-single {
  max-width: 460px;
}
</style>